import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import queryString from 'query-string';
import { Col, Row, Card, Segmented, Carousel, Button, Avatar, Tabs, message, Spin } from 'antd';
import { LoginOutlined } from '@ant-design/icons';
import { loginApi, validSmsApi, pageDetailApi, code2TokenApi } from '../../action';
import { LOGIN_MODE_CONST, USER_TYPE_CONST, FEISHU_ACCESS_URL, DD_ACCESS_URL, AUTH_TOKEN_NAME, BASE_URL, SUB_URL } from '../../const';
import { isMobile } from '../../utils';
import OtherLogin from './otherLogin';
import useEffectOnce from '../../utils/useEffectOnce';
import redirectUrl from "../../utils/redirectUrl";
import syncSubIframe from "../../utils/syncSubIframe";
import SsoUploadTrack from "../../utils/ssoUploadTrack";
import Encryption from "../../utils/encryption";
import AccountLogin from './accountLogin';
import MobileLogin from "./mobileLogin";
import './index.scss';

interface LoginUrlParams {
    id?: string;
    code?: string;
    target?: string;
    epsso_code?: string;
    sp?: string;
    [key: string]: any;
}

// 定义 `page_images` 类型
interface PageImages {
    pc: string[];  // PC 端图片 URL 数组
    mobile: string[];  // 移动端图片 URL 数组
}

// 定义主数据的类型
interface LoginConfig {
    target: string;  // 目标 URL
    login_type: ('user_pass' | 'feishu' | 'dingtalk' | 'message')[];  // 登录方式，包含多个选项
    account_type: (1 | 2)[];  // 账户类型，包含多个选项
    page_images: PageImages;  // 页面图片配置，包含 PC 和移动端图片
}

interface LoginModeTab {
    key: string;
    label: string;
}

// 定义每个对象的类型
interface UserTypeOption {
    label: React.ReactNode;  // label 是一个 React 元素，可以是任何可以渲染的 JSX
    value: 'internal' | 'partime';  // value 是一个字符串，限定为 'internal' 或 'partime'
}

interface LoginRes {
    username?: string;
    password?: string;
    mobile?: string;
    code?: string;
}

interface LoginRef {
    getValues: () => Promise<any>;
}

const judgmentPlatform = (url: string) => {
    const patternPartTime = /backstage\.shihuo\.cn\/parttimePlatform/;
    const patternWebRoot = /backstage\.shihuo\.cn\/webroot/;
    const patternCoreWork = /backstage\.shihuo\.cn\/webroot\/coreWork/;
    if (patternPartTime.test(url)) {
        return 'partTime';
    }
    if (patternCoreWork.test(url)) {
        return 'coreWork';
    }
    if (patternWebRoot) {
        return 'webRoot';
    }
    return '';
}

const feiShuUA = () => {
    const ua = window.navigator.userAgent
    const regex = "Lark\\/([\\d.]+) LarkLocale\\/\\w+"
    return ua.match(regex);
}

const shiHuoUA = () => {
    return navigator.userAgent.toLowerCase().indexOf("shihuo") > -1;
}

const Login: React.FC = (props) => {
    const params: LoginUrlParams = queryString.parse(window.location.search.split('?')[1] || '');
    const navigate = useNavigate();
    const init = useRef<boolean>(false);
    const localInit = useRef<boolean>(false);
    const mobileSide = useRef<boolean>(isMobile());
    const accountLoginRef = useRef<LoginRef>(null);
    const mobileLoginRef = useRef<LoginRef>(null);
    // const carouselParentRef = useRef<any>(null);
    const carouselRef = useRef<any>(null);
    // const initialParentSlide = useRef<number>(mobileSide.current ? 1 : 0);
    const initialSlide = useRef<number>(0);
    const [userType, setUserType] = useState<USER_TYPE_CONST | ''>('internal');
    const [userTypeTab, setUserTypeTab] = useState<UserTypeOption[]>([]);
    const [loginMode, setLoginMode] = useState<LOGIN_MODE_CONST>('account');
    const [loginModeTab, setLoginModeTab] = useState<LoginModeTab[]>([]);
    const [loginConfig, setLoginConfig] = useState<LoginConfig | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [loginLoading, setLoginLoading] = useState<boolean>(false);
    const [loadingFS, setLoadingFS] = useState<boolean>(false);

    useEffectOnce(() => {
        if (params.epsso_code) {
            window.EP_SSO_CODE = params.epsso_code;
        }
        if (!params.target && !params.sp && !params.id) {
            navigate('/error?info=主站禁止直接登录，请打开子站点登录');
        } else {
            if (OtherLogin.checkMainToken({ target: params.target, sp: params.sp, id: params.id })) {
                getPageInfo();
            }
        }
        SsoUploadTrack('sso_page_access', {
            title: '单点登录页面访问',
            target_url: params.target || '',
        })
    });

    useEffect(() => {
        if (init.current) {
            getPageInfo();
        }
    }, [userType]);

    // 用户类型自动选择
    useEffect(() => {
        if (!localInit.current && userTypeTab.length > 0) {
            const SSO_USER_TYPE: any = localStorage.getItem('SSO_USER_TYPE');
            if ((params.target || '').indexOf('backstage.shihuo.cn/parttimePlatform') !== -1 && userTypeTab.filter((item) => item.value === 'partime').length > 0) {
                setUserType('partime');
            } else if (SSO_USER_TYPE && userTypeTab.filter((item) => item.value === SSO_USER_TYPE).length > 0) {
                setUserType(SSO_USER_TYPE);
            }
            localInit.current = true;
        }
    }, [userTypeTab, params])

    // 获取页面信息
    const getPageInfo = useCallback(async () => {
        const { id, target, sp } = params;
        const res = await pageDetailApi({
            id,
            target: encodeURIComponent(target || ''),
            sp,
        });
        if (!feiShuUA()) {
            setLoading(false);
        }
        if (res.data.code === 0) {
            let { account_type, login_type, t_type } = res.data.data || {};
            const platform = judgmentPlatform(target || '');
            // 核心工作系统
            if (platform === 'coreWork') {
                account_type = [1];
                const index = login_type.findIndex((item: string) => item === 'dingtalk');
                if (index != -1) {
                    login_type.splice(index, 1);
                }
            }
            // 兼职平台
            if (userType === 'partime' && platform === 'partTime') {
                login_type = ['user_pass', 'dingtalk'];
            }
            if (userType === 'internal' && platform === 'partTime') {
                const index = login_type.findIndex((item: string) => item === 'dingtalk');
                if (index != -1) {
                    login_type.splice(index, 1);
                }
            }
            if (userType === 'partime' && platform === 'webRoot') {
                login_type = ['user_pass', 'dingtalk'];
            }
            if (userType === 'internal' && platform === 'webRoot') {
                const index = login_type.findIndex((item: string) => item === 'dingtalk');
                if (index != -1) {
                    login_type.splice(index, 1);
                }
            }
            const modeTab: LoginModeTab[] = [];
            if ((login_type || []).includes('user_pass')) {
                modeTab.push({ key: 'account', label: '账号登录' })
            }
            if ((login_type || []).includes('message')) {
                modeTab.push({ key: 'mobile', label: '手机短信登录' })
            }
            // 如果只有短信登录权限，无账户密码登录权限
            if ((login_type || []).includes('message') && !(login_type || []).includes('user_pass')) {
                carouselRef.current && carouselRef.current.goTo(1);
            } else {
                carouselRef.current && carouselRef.current.goTo(0);
            }
            const userTab: UserTypeOption[] = [];
            if ((account_type || []).includes(1)) {
                userTab.push({
                    label: (
                        <div className="sso-login-user-label">
                            <div>
                                <p>内</p>
                                <p>部</p>
                            </div>
                        </div>
                    ),
                    value: 'internal'
                })
            }
            if ((account_type || []).includes(2)) {
                userTab.push({
                    label: (
                        <div className="sso-login-user-label">
                            <div>
                                <p>兼</p>
                                <p>职</p>
                            </div>
                        </div>
                    ),
                    value: 'partime'
                })
            }
            if ((account_type || []).includes(2) && !(account_type || []).includes(1)) {
                setUserType('partime');
            }
            if ((account_type || []).includes(1) && !(account_type || []).includes(2)) {
                setUserType('internal');
            }
            if (t_type.includes('v1')) {
                window.SSO_TOKEN_VERSION = 'v1';
            }
            if (t_type.includes('v2')) {
                window.SSO_TOKEN_VERSION = 'v2';
            }
            // 如果是飞书应用，直接登录
            if (feiShuUA()) {
                handleFeiShuLogin();
                return false;
            }
            setLoginMode('account');
            setUserTypeTab(userTab);
            setLoginConfig({
                ...res.data.data,
                login_type
            });
            setLoginModeTab(modeTab);
            init.current = true;
        } else {
            SsoUploadTrack('sso_page_info_error', {
                title: '单点登录子站信息获取失败',
                target_url: params.target || '',
                error_info: res.data.msg
            })
            navigate(`/error?info=子站信息获取失败,联系运维：${res.data.msg}`);
            message.open({
                type: 'error',
                content: res.data.msg,
            });
        }
    }, [params, userType, carouselRef]);

    // code换token
    const getCode2Token = useCallback(async (code: string) => {
        const res = await code2TokenApi({
            code,
        });
        if (res.data.code === 0) {
            localStorage.setItem(`${AUTH_TOKEN_NAME}`, res.data.data);
            SsoUploadTrack('sso_page_login_success', {
                title: '单点登录成功',
                target_url: params.target || '',
                method: loginMode,
                token: res.data.data
            });
            syncSubIframe(res.data.data, params?.target || loginConfig?.target, (target: string) => {
                if (OtherLogin.loginOtherSite({ target: params.target, sp: params.sp, id: params.id })) {
                    redirectUrl({
                        target
                    });
                }
            });
        } else {
            SsoUploadTrack('sso_page_code2token_error', {
                title: '单点登录code换token失败',
                method: loginMode,
                target_url: params.target || '',
                error_info: res.data.msg
            })
            setLoginLoading(false);
            message.open({
                type: 'error',
                content: res.data.msg,
            });
        }
    }, [params, loginMode, loginConfig]);

    // 选择账户类型
    const handleSelectUserType = useCallback((value: USER_TYPE_CONST | '') => {
        localStorage.setItem('SSO_USER_TYPE', value);
        setUserType(value);
        // if (['internal', 'partime'].includes(value)) {
        //     // carouselParentRef.current.next();
        // }
        // if (!value) {
        //     // carouselParentRef.current.prev();
        // }
    }, []);

    // 选择登录方式
    const handleSelectMode = useCallback((value: LOGIN_MODE_CONST) => {
        setLoginMode(value);
        if (value === 'account') {
            carouselRef.current.goTo(0);
        }
        if (value === 'mobile') {
            carouselRef.current.goTo(1);
        }
    }, [carouselRef]);

    // 登录
    const handleLoginBtn = useCallback(async () => {
        let res: LoginRes | any = null;
        let loginRes: any | null = null;
        // 账号登录
        if (loginMode === 'account' && accountLoginRef.current) {
            res = await accountLoginRef.current.getValues();
            setLoginLoading(true);
            const encryptionStr = await Encryption({
                target: encodeURIComponent(params.target || loginConfig?.target || ''),
            }, JSON.stringify({
                username: res?.username,
                password: res?.password,
            }));
            if (!encryptionStr) {
                setLoginLoading(false);
                return false;
            }
            if (shiHuoUA()) {
                window.Jockey.send(
                    "request",
                    {
                        type: "POST",
                        url: `${BASE_URL}/api/login`,
                        data: {
                            pass_info: encryptionStr,
                            target: encodeURIComponent(params.target || loginConfig?.target || ''),
                            account_type: userType === 'internal' ? 1 : 2
                        },
                        noDecode: true,
                        header: { 'Content-Type': 'application/json', },
                    },
                    function (res: any) {
                        const body = res ? res.response.body : null;
                        if (!body) {
                            setLoginLoading(false);
                            return false;
                        }
                        const resData = typeof body == "string" ? JSON.parse(body) : body;
                        if (resData && resData.data) {
                            if (resData.code != 0) {
                                setLoginLoading(false);
                                message.open({
                                    type: 'error',
                                    content: resData.msg,
                                });
                                return false;
                            }
                            getCode2Token(resData?.data?.code);
                        }
                    }
                );
                return false;
            }
            loginRes = await loginApi({
                // username: res?.username,
                // password: res?.password,
                pass_info: encryptionStr,
                target: encodeURIComponent(params.target || loginConfig?.target || ''),
                account_type: userType === 'internal' ? 1 : 2
            });
        }
        // 短信登录
        if (loginMode === 'mobile' && mobileLoginRef.current) {
            res = await mobileLoginRef.current.getValues();
            setLoginLoading(true);
            if (shiHuoUA()) {
                window.Jockey.send(
                    "request",
                    {
                        type: "POST",
                        url: `${BASE_URL}/api/validSms`,
                        data: {
                            mobile: res?.mobile,
                            code: res?.code,
                            target: encodeURIComponent(params.target || loginConfig?.target || ''),
                            account_type: userType === 'internal' ? 1 : 2
                        },
                        header: { 'Content-Type': 'application/json', },
                    },
                    function (res: any) {
                        const body = res ? res.response.body : null;
                        if (!body) {
                            setLoginLoading(false);
                            return false;
                        }
                        const resData = typeof body == "string" ? JSON.parse(body) : body;
                        if (resData.code != 0) {
                            setLoginLoading(false);
                            message.open({
                                type: 'error',
                                content: resData.msg,
                            });
                            return false;
                        }
                        getCode2Token(resData?.data?.code);
                    }
                );
                return false;
            }
            loginRes = await validSmsApi({
                mobile: res?.mobile,
                code: res?.code,
                target: encodeURIComponent(params.target || loginConfig?.target || ''),
                account_type: userType === 'internal' ? 1 : 2
            });
        }
        if (loginRes.data.code === 0) {
            getCode2Token(loginRes.data?.data?.code);
        } else {
            SsoUploadTrack('sso_page_login_error', {
                title: '单点登录获取code失败',
                method: loginMode,
                target_url: params.target || '',
                error_info: loginRes.data.msg
            })
            setLoginLoading(false);
            message.open({
                type: 'error',
                content: loginRes.data.msg,
            });
        }
    }, [getCode2Token, params, loginMode, userType, loginConfig, accountLoginRef, mobileLoginRef]);

    // 飞书登录
    const handleFeiShuLogin = useCallback(() => {
        setLoadingFS(true);
        let url = '';
        if (params.sp || params.id) {
            url = `${FEISHU_ACCESS_URL}&redirect_uri=${encodeURIComponent(`${window.location.origin}/callback/feishu?${queryString.stringify(params)}&USER_TYPE=${userType}`)}`;
        } else {
            url = `${FEISHU_ACCESS_URL}&redirect_uri=${encodeURIComponent(`${window.location.origin}/callback/feishu?target=${encodeURIComponent(params.target || loginConfig?.target || '')}&USER_TYPE=${userType}`)}`;
        }
        SsoUploadTrack('sso_page_feishu_url', {
            title: '单点登录飞书授权页',
            method: loginMode,
            target_url: params.target || '',
            feishu_url: url
        })
        window.location.replace(url);
    }, [params, userType, loginConfig]);

    // 钉钉登录
    const handleDdLogin = useCallback(() => {
        const url = `${DD_ACCESS_URL}&redirect_uri=${encodeURIComponent(`${window.location.origin}/callback/dd?target=${encodeURIComponent(params.target || loginConfig?.target || '')}&USER_TYPE=${userType}`)}`;
        window.location.replace(url);
    }, [params, userType, loginConfig]);

    // 是否为仅支持三方登录模式
    const isSpecialMode = useMemo(() => {
        return !(loginConfig?.login_type.includes('message') || loginConfig?.login_type.includes('user_pass'));
    }, [loginConfig]);

    // const userTypeName = useMemo(() => {
    //     if (userType === 'internal') {
    //         return <Tag icon={<UserOutlined />} color="red">{USER_TYPE_ENUM['internal']}</Tag>;
    //     }
    //     if (userType === 'partime') {
    //         return <Tag icon={<UserOutlined />} color="blue">{USER_TYPE_ENUM['partime']}</Tag>;
    //     }
    //     return <Tag icon={<LoadingOutlined />} />;
    // }, [userType])

    if (loading) {
        return <Spin size="large" style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
    }

    return (
        <div className={`sso-login-body${mobileSide.current ? ' mobile' : ''}`}>
            <Row>
                {!mobileSide.current ? <Col span={16}>
                    <div className="sso-login-left">
                        <div style={{ width: '85%' }}>
                            <Carousel adaptiveHeight effect="fade" dots={false} >
                                {
                                    loginConfig?.page_images.pc.map((item) => {
                                        return <div key={item}>
                                            <img src={item} alt="轮播" />
                                        </div>
                                    })
                                }
                            </Carousel>
                        </div>
                    </div>
                </Col> : null}
                <Col span={mobileSide.current ? 24 : 8}>
                    <div className={`sso-login-right${mobileSide.current ? ' mobile' : ''}`} style={mobileSide.current ? { backgroundImage: `url(${loginConfig?.page_images.mobile})` } : {}}  >
                        {!mobileSide.current &&
                            <div className="sso-login-logo">
                                <img src="//static.shihuocdn.cn/admin/files/20210909/1587b41276a29171ea2fca3a8d275c99.png" alt="背景" />
                            </div>
                        }
                        {
                            isSpecialMode ? <Card className="sso-login-card" bordered={false} style={{ background: 'unset', boxShadow: 'unset' }}>
                                <div className="special-third-party-content">
                                    {
                                        loginConfig?.login_type.includes('feishu') && <div className="special-third-party-icons" onClick={handleFeiShuLogin}>
                                            <Avatar
                                                size={80}
                                                // style={{ border: '1px solid #bebebe' }}
                                                src={<img className="icons-img" src="//static.shihuocdn.cn/admin/files/20220114/abf7f3566725238c62750fe8319f4d3d.png" title="飞书" alt="飞书" />}
                                            />
                                            <span className="special-third-party-title">飞书登录</span>
                                        </div>
                                    }
                                    {
                                        loginConfig?.login_type.includes('dingtalk') && <div className="special-third-party-icons" onClick={handleDdLogin}>
                                            <Avatar
                                                size={80}
                                                // style={{ border: '1px solid #bebebe' }}
                                                src={<img className="icons-img" src="//static.shihuocdn.cn/admin/files/20230731/3c302db285c007b89676ca4e2f4d78bd.png" title="钉钉" alt="钉钉" />}
                                            />
                                            <span className="special-third-party-title">钉钉登录</span>
                                        </div>
                                    }
                                </div>
                            </Card> :
                                <Card className="sso-login-card" bordered={true}>
                                    {
                                        !mobileSide.current && <div className="sso-login-user">
                                            <Segmented
                                                size="middle"
                                                vertical
                                                options={userTypeTab}
                                                value={userType}
                                                onChange={handleSelectUserType}
                                            />
                                        </div>
                                    }
                                    <Tabs size="large" activeKey={loginMode} items={loginModeTab} onChange={handleSelectMode} />
                                    <Carousel ref={carouselRef} dots={false} effect="fade" adaptiveHeight={true} infinite={false} initialSlide={initialSlide.current} easing="">
                                        <div className="sso-login-account">
                                            <AccountLogin ref={accountLoginRef} />
                                        </div>
                                        <div className="sso-login-mobile">
                                            <MobileLogin ref={mobileLoginRef} target={params.target || loginConfig?.target || ''} />
                                        </div>
                                    </Carousel>
                                    <div className="sso-login-footer">
                                        <Button size="large" style={{ width: '100%' }} iconPosition="end" icon={<LoginOutlined />} type="primary" loading={loginLoading} onClick={handleLoginBtn}>登录</Button>
                                        <div className={`sso-login-third-party${mobileSide.current ? ' mobile' : ''}`}>
                                            {
                                                mobileSide.current ? <Row gutter={10}>
                                                    <Col span={24}>
                                                        <Button loading={loadingFS} size="large" style={{ width: '100%' }} type="default" onClick={handleFeiShuLogin}>
                                                            <img className="third-party-icon" alt="飞书" src="//static.shihuocdn.cn/admin/files/20220114/abf7f3566725238c62750fe8319f4d3d.png" />
                                                            <span className="third-party-text">飞书登录</span>
                                                        </Button>
                                                    </Col>
                                                    {/* <Col span={12}>
                                                <Button size="large" style={{ width: '100%' }} type="default">
                                                    <img className="third-party-icon" alt="钉钉" src="//static.shihuocdn.cn/admin/files/20230731/3c302db285c007b89676ca4e2f4d78bd.png" />
                                                    <span className="third-party-text">
                                                        <span>钉钉登录</span>
                                                        <span>兼职登录入口</span>
                                                    </span>
                                                </Button>
                                            </Col> */}
                                                </Row> : <div className="third-party-content">
                                                    <div className="third-party-title">
                                                        <div className="line" />
                                                        更多登录方式
                                                        <div className="line" />
                                                    </div>
                                                    <div className="third-party-icons">
                                                        {
                                                            loginConfig?.login_type.includes('feishu') && <div className="third-party-icons-item" onClick={handleFeiShuLogin}>
                                                                <Avatar
                                                                    size={40}
                                                                    src={<img className="icons-img" src="//static.shihuocdn.cn/admin/files/20220114/abf7f3566725238c62750fe8319f4d3d.png" title="飞书" alt="飞书" />}
                                                                />
                                                                <span className="third-party-title">飞书登录</span>
                                                            </div>
                                                        }
                                                        {
                                                            loginConfig?.login_type.includes('dingtalk') && <div className="third-party-icons-item" onClick={handleDdLogin}>
                                                                <Avatar
                                                                    size={40}
                                                                    src={<img className="icons-img" src="//static.shihuocdn.cn/admin/files/20230731/3c302db285c007b89676ca4e2f4d78bd.png" title="钉钉" alt="钉钉" />}
                                                                />
                                                                <span className="third-party-title">钉钉登录</span>
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </Card>
                        }
                        {
                            !mobileSide.current && <div className="sso-login-message">
                                <div className="sso-login-security">
                                    ⚠️ 您登录的系统已留存日志，如非法获取敏感数据，将依照法律处理。
                                </div>
                            </div>
                        }
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default Login;